import type TauriDatabase from "@tauri-apps/plugin-sql";
import { type Database as BunDatabase } from "bun:sqlite";
import { type Operation, applyPatch } from "fast-json-patch/index.mjs";
import { List, Map } from "immutable";

import { type BaseSpec } from "../../api-types";
import { type ActionIntent } from "../QueryEvaluator/ActionIntent";
import { type QueryEvaluatorState } from "../QueryEvaluator/QueryManagerState";
import { RunnableQuery } from "../RunnableQuery";
import { sql } from "../SqlString";
import { type QueryPersistence } from "../Store/QueryPersistence";
import { reactionTable } from "../Store/base-utils";
import { Tuple } from "../Tuple";
import { ZSet } from "../ZSet";
import { contentId } from "../operatorsV2/QuerySpec";
import { type SqliteColumnDef, type SqliteColumnType, type SqliteTableDef } from "../schema";
import { runGetBaseMetadata } from "./baseMetadataTable";
import { makeReactionTableName } from "./makeReactionTableName";
import { runColumnAppendMigration } from "./runColumnAppendMigration";
import { runColumnDeleteMigration } from "./runColumnDeleteMigration";
import { runColumnUpdateMigration } from "./runColumnUpdateMigration";
import { runCreateTableIfNotExists } from "./runCreateTableIfNotExists";
import { runGetAllRecords } from "./runGetAllRecords";
import { runGetTableXInfo } from "./runGetTableXInfo";
import { upsertSql } from "./upsertSql";

export const queryEvaluatorStateFromSqlite = async (
	db: BunDatabase | TauriDatabase,
	baseSpec: BaseSpec
): Promise<QueryEvaluatorState> => {
	const meta = await runGetBaseMetadata(db);

	// Create initial tables from base spec
	let tables = Map<string, ZSet<unknown>>();
	for (const [tableName, tableSpec] of Object.entries(baseSpec.tables)) {
		const desiredTable: SqliteTableDef = { tableName, columns: tableSpec.columns };
		await runCreateTableIfNotExists(db, desiredTable, tableSpec.defaultData);
		const currentTableInfo = await runGetTableXInfo(db, tableName);
		const primaryKeyCol = currentTableInfo.columns.find((col) => col.pk === 1);
		const records = await runGetAllRecords(db, tableName);
		tables = tables.set(
			tableName,
			ZSet.from<unknown>(
				records.map((record) => {
					// FIXME: `id` does not exist in general on this?
					// @ts-ignore
					const id = record?.[primaryKeyCol?.name ?? "id"];
					// TODO: const owner = record?.["owner"]; or something similar
					return { tuple: Tuple.from(id, undefined, record), weight: 1 };
				})
			)
		);
	}

	let querySpecs = Map<string, RunnableQuery>();
	for (const viewSpec of Object.values(baseSpec.views)) {
		for (const subquerySpec of viewSpec.query.subquerySpecs) {
			querySpecs = querySpecs.set(
				contentId(subquerySpec),
				RunnableQuery.fromSpec(subquerySpec, { type: "read_all_rows" })
			);
		}

		const spec = viewSpec.query.spec;
		querySpecs = querySpecs.set(
			contentId(viewSpec.query.spec),
			RunnableQuery.fromSpec(spec, { type: "read_all_rows" })
		);
	}

	return {
		currentEpoch: meta.epoch,
		tables,
		querySpecs,
		error: null,

		// FIXME: Delete these??
		queryStates: Map(),
		queryResults: Map(),
		actionLog: List(),
	};
};

export class SqlitePersistence implements QueryPersistence {
	private _currentState: QueryEvaluatorState;

	constructor(
		private readonly db: BunDatabase | TauriDatabase,
		initialState: QueryEvaluatorState
	) {
		this._currentState = initialState;
	}

	localStateReplica(): QueryEvaluatorState {
		return this._currentState;
	}

	async ensurePersistedState(actionIntent: ActionIntent): Promise<void> {
		const { action } = actionIntent;
		switch (action.type) {
			case "queryRegistration":
				// NOTE: This is advisory for the local-only case.

				this._currentState = actionIntent.proposedState;
				break;
			case "mutation": {
				const { table, mutation } = action;

				// Get table info to handle the mutation
				const tableInfo = await runGetTableXInfo(this.db, table);
				const primaryKeyCol = tableInfo.columns.find((col) => col.pk === 1);
				if (!primaryKeyCol) {
					throw new Error(`Table ${table} has no primary key column`);
				}

				switch (mutation.type) {
					case "INSERT": {
						const { recordId, record } = mutation;
						const data = record.getValue() as Record<string, unknown>;
						const tableDef: SqliteTableDef = {
							tableName: table,
							columns: tableInfo.columns.map((col) => ({
								name: col.name,
								type: col.type as SqliteColumnType,
								nullable: col.notnull === 0,
								primaryKey: col.pk === 1,
								unique: col.pk === 1,
							})),
						};
						const { statement, params } = upsertSql(tableDef, {
							[primaryKeyCol.name]: recordId,
							...data,
						});
						if ("execute" in this.db) {
							await this.db.execute(statement, params);
						} else {
							this.db.prepare(statement, params).run();
						}
						break;
					}
					case "UPDATE": {
						const { recordId, change } = mutation;
						// Ensure change has required properties
						if (change.op === "add" && change.value === undefined) {
							throw new Error("Add operation requires a value");
						}
						if (change.op === "replace" && change.value === undefined) {
							throw new Error("Replace operation requires a value");
						}

						// Get table info to handle the update
						const tableInfo = await runGetTableXInfo(this.db, table);
						const primaryKeyCol = tableInfo.columns.find((col) => col.pk === 1);
						if (!primaryKeyCol) {
							throw new Error(`Table ${table} has no primary key column`);
						}

						// Get current record state
						const { statement: getStmt, params: getParams } = sql`SELECT *
FROM ${sql.id(table)}
WHERE ${sql.id(primaryKeyCol.name)} = ${recordId}`;
						let currentRecord: Record<string, unknown>;
						if ("execute" in this.db) {
							const result = await this.db.select(getStmt, getParams);
							// @ts-ignore
							currentRecord = result?.[0] as Record<string, unknown>;
						} else {
							const result = this.db.prepare(getStmt, getParams).get();
							currentRecord = result as Record<string, unknown>;
						}

						if (!currentRecord) {
							throw new Error(`Record ${recordId} not found in table ${table}`);
						}

						// Apply the patch to the current record
						const patchResult = applyPatch(currentRecord, [change as Operation]);
						if (!patchResult[0]) {
							throw new Error("Failed to apply patch");
						}
						const updatedRecord = patchResult[0].newDocument;

						// Create table def for upsert
						const tableDef: SqliteTableDef = {
							tableName: table,
							columns: tableInfo.columns.map((col) => ({
								name: col.name,
								type: col.type as SqliteColumnType,
								nullable: col.notnull === 0,
								primaryKey: col.pk === 1,
								unique: col.pk === 1,
							})),
						};

						const { statement, params } = upsertSql(tableDef, updatedRecord);
						if ("execute" in this.db) {
							await this.db.execute(statement, params);
						} else {
							this.db.prepare(statement, params).run();
						}
						break;
					}
					case "DELETE": {
						const { recordId } = mutation;
						const statement = `DELETE FROM ${table} WHERE ${primaryKeyCol.name} = ?`;
						if ("execute" in this.db) {
							await this.db.execute(statement, [recordId]);
						} else {
							this.db.prepare(statement, [recordId]).run();
						}
						break;
					}
				}

				// Update the state after successful SQLite operation
				this._currentState = actionIntent.proposedState;
				break;
			}
			case "columnAppend": {
				const { table, sqliteColumnDef: column } = action;
				await runColumnAppendMigration(this.db, table, column);

				break;
			}
			case "columnDelete": {
				const { table, columnName } = action;
				await runColumnDeleteMigration(this.db, table, columnName);
				break;
			}
			case "columnRename": {
				const { table, oldColumnName, newColumnName } = action;

				// Get current table info to preserve column properties
				const tableInfo = await runGetTableXInfo(this.db, table);
				const oldColumn = tableInfo.columns.find((col) => col.name === oldColumnName);
				if (!oldColumn) {
					throw new Error(`Column ${oldColumnName} not found in table ${table}`);
				}

				// Check if this is a Reaction column rename by looking at the current state
				// We need to check if there's a reaction table for the old column name
				const oldReactionTableName = makeReactionTableName(table, oldColumnName);
				const isReactionColumn = this._currentState.tables.has(oldReactionTableName);

				if (isReactionColumn) {
					// Handle reaction table rename
					const newReactionTableName = makeReactionTableName(table, newColumnName);

					// Check if the old reaction table exists
					try {
						const oldReactionTableInfo = await runGetTableXInfo(
							this.db,
							oldReactionTableName
						);

						// Create the new reaction table
						const newReactionTableDef: SqliteTableDef =
							reactionTable(newReactionTableName);
						await runCreateTableIfNotExists(this.db, newReactionTableDef);

						// Migrate data from old reaction table to new reaction table
						const migrateStatement = `INSERT INTO ${newReactionTableName} SELECT * FROM ${oldReactionTableName}`;
						if ("execute" in this.db) {
							await this.db.execute(migrateStatement);
						} else {
							this.db.prepare(migrateStatement).run();
						}

						// Drop the old reaction table
						const dropStatement = `DROP TABLE ${oldReactionTableName}`;
						if ("execute" in this.db) {
							await this.db.execute(dropStatement);
						} else {
							this.db.prepare(dropStatement).run();
						}
					} catch (error) {
						// Old reaction table doesn't exist, which is fine
						console.info(
							`Old reaction table ${oldReactionTableName} doesn't exist, skipping migration`
						);
					}
				}

				// Create updated column definition with new name but same properties
				const updatedColumn: SqliteColumnDef = {
					name: newColumnName,
					type: oldColumn.type as SqliteColumnType,
					nullable: oldColumn.notnull === 0,
					primaryKey: oldColumn.pk === 1,
					unique: oldColumn.pk === 1,
				};

				// Run the migration to rename the column
				await runColumnUpdateMigration(this.db, table, oldColumnName, updatedColumn);

				// Update the state after successful SQLite operation
				this._currentState = actionIntent.proposedState;
				break;
			}
			case "columnTypeChange": {
				const { table, columnName, oldTypeInfo, newTypeInfo } = action;

				// Create the reaction table name
				const reactionTableName = makeReactionTableName(table, columnName);

				// Create the reaction table
				const reactionTableDef: SqliteTableDef = reactionTable(reactionTableName);

				// Create the reaction table
				await runCreateTableIfNotExists(this.db, reactionTableDef);

				// Get current table info to preserve column properties
				const tableInfo = await runGetTableXInfo(this.db, table);
				const oldColumn = tableInfo.columns.find((col) => col.name === columnName);
				if (!oldColumn) {
					throw new Error(`Column ${columnName} not found in table ${table}`);
				}

				// Create updated column definition with new type info
				const updatedColumn: SqliteColumnDef = {
					name: columnName,
					type: oldColumn.type as SqliteColumnType,
					nullable: oldColumn.notnull === 0,
					primaryKey: oldColumn.pk === 1,
					unique: oldColumn.pk === 1,
					typeInfo: newTypeInfo,
				};

				// Run the migration to update the column type
				await runColumnUpdateMigration(this.db, table, columnName, updatedColumn);

				// Update the state after successful SQLite operation
				this._currentState = actionIntent.proposedState;
				break;
			}
			default: {
				const _: never = action;
				throw new Error(`Unknown action intent type: ${_}`);
			}
		}
	}
}
